import RetryError from "@app/components/core/RetryError"
import { useRaiseError } from "@app/store/actions/errorActions"
import { setSelectedDay } from "@app/store/actions/orderActions"
import { useGetStrings } from "@app/store/service/cmsService"
import { useCalendar } from "@app/store/service/connectorService"
import { appStore } from "@app/store/store"
import { getLongDateString, getPriceString } from "@app/utils/localeUtils"
import { emptyArray } from "@app/utils/objectUtils"
import { selectVisitorKindIds } from "@app/utils/selectors"
import { Calendar, useViewport } from "@tecreation/funnel-components"
import { createMemoizedSelector } from "@tecreation/js-state"
import { useSelector } from "@tecreation/js-state-react"
import { useCallback, useEffect, useRef, useState } from "react"
import Skeleton from "react-loading-skeleton"

export default function DateSelectionCalendar() {
	const selectedDayDate = useSelector(state => (state.order.selectedDay ? state.order.selectedDay.dateObj : undefined))
	const selectedDay = useSelector(state => state.order.selectedDay)

	const locale = useSelector(state => state.core.locale)
	const locationId = useSelector("core.locationId")
	const zoneId = useSelector("core.zoneId")
	const visitorKindIds = useSelector(selectVisitorKindIds)
	const [getCalendar, { isFetching: loadingCalendar, isUninitialized: unintializedCalendar, data: calendarData, error: calendarError }] = useCalendar()
	const {
		data: { dateSelectionIsClosed, dateSelectionSelectOtherDate, buttonLabelNextMonth, buttonLabelPreviousMonth, dateSelectionLabel },
	} = useGetStrings(undefined, { defaultData: {} })

	const htgValidTo = useSelector(
		createMemoizedSelector("core.htgGuestData", guestData => {
			let date = undefined
			if (guestData && guestData.guestcards) {
				for (let card of guestData.guestcards) {
					if (!date) {
						if (card.guestcardValidUntil) {
							let x = new Date(card.guestcardValidUntil)
							date = x
						}
					} else {
						if (card.guestcardValidUntil) {
							let x = new Date(card.guestcardValidUntil)
							if (x.getTime() <= date.getTime()) {
								date = x
							}
						}
					}
				}
				return date
			}
		}),
	)

	const { sm, md } = useViewport()

	let startDate = new Date()
	if (selectedDayDate) {
		startDate = new Date(selectedDayDate)
		startDate.setDate(1)
	}
	const [currentMonth, setCurrentMonth] = useState(startDate)
	const kioskMode = useSelector(state => state.core.kioskMode)
	const raiseError = useRaiseError()

	const [showCalendar, setShowCalendar] = useState(false)
	const lastFocusedElement = useRef(null)
	const ariaLabelTranslations = { buttonLabelNextMonth, buttonLabelPreviousMonth }

	const fetchCalendar = useCallback(() => {
		let thisDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)

		;(async () => {
			let d1 = new Date(currentMonth)
			thisDate.setMonth(thisDate.getMonth() + (sm || md ? 3 : 0))

			if (htgValidTo) {
				if (thisDate.getTime() >= htgValidTo.getTime()) {
					thisDate = htgValidTo
				}
			}

			try {
				await getCalendar({
					locationId,
					zoneId,
					visitorKindIds,
					dateFrom: d1.toISOString().slice(0, 10),
					dateTo: thisDate.toISOString().slice(0, 10),
				}).unwrap()
			} catch (e) {
				raiseError(e, undefined, false, true)
			}
		})()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentMonth, sm, md, htgValidTo])

	useEffect(() => {
		fetchCalendar()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentMonth, sm, md])

	/**
	 * if a selectedDay has value reload (possible if we come from the saunaboat) then we reselect it after loading so we have a valid dateObj
	 */
	useEffect(() => {
		if (selectedDay && calendarData && selectedDay.reload === true) {
			const f = calendarData.find(e => e.date.toDateString() === selectedDay.dateObj.toDateString())
			setSelectedDay(f)
		}
		if (calendarData) {
			appStore.set("order.calendarData", calendarData)
		}
	}, [calendarData, selectedDay])

	useEffect(() => {
		if (selectedDay) {
			setShowCalendar(false)
		}
	}, [selectedDay])

	useEffect(() => {
		if (kioskMode && calendarData && !selectedDayDate) {
			let today = new Date().toISOString()
			today = today.slice(0, 10)
			let founded = calendarData.find(e => e.dateString === today)
			if (founded) {
				setSelectedDay(founded)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kioskMode, calendarData])

	const onDaySelectedThroughKeyboard = () => {
		lastFocusedElement.current = document.activeElement

		/**
		 * Note: we need the delay here because DOM has not been updated with
		 * the timeBlocks yet (on first render)
		 */
		setTimeout(() => {
			const ticketstackContainer = document.querySelector("[data-ticketstack-container]")
			if (ticketstackContainer) {
				const firstFocusable = ticketstackContainer.querySelector(
					'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
				)
				if (firstFocusable) {
					firstFocusable.focus()
				}
			}
		}, 500)
	}

	// Expose method to restore focus when coming back from DateSelectionTicketstack (keyboard navigation)
	useEffect(() => {
		window.restoreDateSelectionCalendarFocus = () => {
			if (lastFocusedElement.current) {
				lastFocusedElement.current.focus()
			}
		}
		return () => {
			delete window.restoreDateSelectionCalendarFocus
		}
	}, [])

	if (kioskMode && calendarData && !selectedDayDate) {
		return <div className="text-center f2 color-fg-danger">{dateSelectionIsClosed}</div>
	}

	if (kioskMode && !showCalendar) {
		if (selectedDayDate) {
			return (
				<div className="px-3">
					<div className="f3">{getLongDateString(selectedDayDate)}</div>
					<div
						className="color-fg-secondary f4 mt-2 clickable"
						onClick={() => {
							setShowCalendar(true)
						}}
					>
						{dateSelectionSelectOtherDate}
					</div>
				</div>
			)
		} else {
			return null
		}
	}

	if (calendarError) {
		return <RetryError show loading={loadingCalendar} onRetry={fetchCalendar} />
	}

	return (
		<div role="region" aria-label={dateSelectionLabel}>
			<Calendar
				date={selectedDayDate}
				locale={locale}
				disableMonthChange={md || sm}
				onMonthChange={e => {
					setCurrentMonth(e)
				}}
				onKeyboardSelect={onDaySelectedThroughKeyboard}
				ariaLabelTranslations={ariaLabelTranslations}
				additionalDateInfo={date => {
					if (loadingCalendar || unintializedCalendar || !calendarData) {
						return (
							<div style={{ width: "30px" }} className="f6">
								<Skeleton />
							</div>
						)
					}
					const f = calendarData.find(e => e.date.toDateString() === date.toDateString())
					if (f) {
						if (f.message) return null

						const { color, price } = f
						return (
							<div style={{ color: color }} className="f5 color-fg-secondary">
								{getPriceString(price, 0)}
							</div>
						)
					}
					return null
				}}
				isDisabled={date => {
					if (loadingCalendar || unintializedCalendar || !calendarData) {
						return false
					}
					const f = calendarData.find(e => e.date.toDateString() === date.toDateString())
					if (f) {
						if (f.message) return false
						return f.restCount > 0
					}
					return false
				}}
				isSelectable={() => {
					if (loadingCalendar || unintializedCalendar || !calendarData) {
						return false
					}

					return true
				}}
				onDateChange={date => {
					const f = calendarData.find(e => e.date.toDateString() === date.toDateString())
					if (!f) {
						setSelectedDay({ date: date, dateString: date.toISOString(), price: 0, restCount: -1, exists: false })
					} else {
						setSelectedDay(f)
					}
				}}
			/>
			{md || sm ? (
				<>
					{emptyArray(3).map((__, i) => {
						let d = selectedDayDate ? new Date(selectedDayDate) : new Date()
						return (
							<Calendar
								key={i}
								date={selectedDayDate}
								locale={locale}
								disableMonthChange={md || sm}
								onMonthChange={e => {
									setCurrentMonth(e)
								}}
								onKeyboardSelect={onDaySelectedThroughKeyboard}
								monthDate={new Date(d.getFullYear(), d.getMonth() + (i + 1), 1)}
								ariaLabelTranslations={ariaLabelTranslations}
								additionalDateInfo={date => {
									if (loadingCalendar || unintializedCalendar || !calendarData) {
										return (
											<div style={{ width: "30px" }} className="f6">
												<Skeleton />
											</div>
										)
									}
									const f = calendarData.find(e => e.date.toDateString() === date.toDateString())
									if (f) {
										if (f.message) return null

										const { color, price } = f
										return (
											<div style={{ color: color }} className="f5 color-fg-secondary">
												{getPriceString(price, 0)}
											</div>
										)
									}
									return null
								}}
								isDisabled={date => {
									if (loadingCalendar || unintializedCalendar || !calendarData) {
										return false
									}
									const f = calendarData.find(e => e.date.toDateString() === date.toDateString())
									if (f) {
										if (f.message) return false
										return f.restCount > 0
									}
									return false
								}}
								isSelectable={() => {
									if (loadingCalendar || unintializedCalendar || !calendarData) {
										return false
									}

									return true
								}}
								onDateChange={date => {
									const f = calendarData.find(e => e.date.toDateString() === date.toDateString())
									if (!f) {
										setSelectedDay({ date: date, dateString: date.toISOString(), price: 0, restCount: -1, exists: false })
									} else {
										setSelectedDay(f)
									}
								}}
							/>
						)
					})}
				</>
			) : null}
		</div>
	)
}
